import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import type React from "react";
import { useRef } from "react";
import { DropdownItem } from "./dropdown";

// Defaults tuned for the navbar selector dropdowns (Schemes, Companies).
// Each matches a concrete layout constraint — change only after verifying the
// selectors still render cleanly in privacy mode (where labels can be long).
// - MAX_HEIGHT_PX: caps the dropdown body height; items past this scroll.
// - MIN_WIDTH_PX: DropdownMenu's `w-max` can't measure `position: absolute`
//   virtual rows, so without a lower bound the menu collapses to the trigger
//   button width. 320px fits a typical obfuscated "test-<hash>" label on one
//   line.
// - ESTIMATE_SIZE_PX: matches DropdownItem's `py-2.5 sm:py-1.5` at text-sm;
//   each virtual row is pinned to exactly this height so wrapped content
//   never overlaps its neighbour.
// - OVERSCAN: small buffer of rows rendered above/below the visible window
//   to avoid blank frames during fast scrolling.
const MAX_HEIGHT_PX = 320;
const MIN_WIDTH_PX = 320;
const ESTIMATE_SIZE_PX = 36;
const OVERSCAN = 8;

export interface VirtualizedDropdownListProps<T> {
  items: T[];
  getItemKey: (item: T) => string;
  renderLabel: (item: T) => React.ReactNode;
  onSelect: (item: T) => void;
  isSelected?: (item: T) => boolean;
  itemTestId?: (item: T) => string;
  emptyMessage?: string;
  maxHeight?: number;
  /**
   * Explicit min-width in px. Required because the absolute-positioned
   * virtual rows are out of flow, so the parent dropdown's `w-max` cannot
   * measure them and would collapse to the selector button's width.
   */
  minWidth?: number;
  estimateSize?: number;
  /**
   * Accessible name for the scrollable list region, announced by screen
   * readers when the user enters the dropdown body.
   */
  "aria-label"?: string;
}

// Must be rendered inside a Headless DropdownMenu (role="menu"). Each item is
// a Headless.MenuItem so keyboard selection works on the currently-rendered
// window; items outside the rendered window are not reachable by arrow keys
// (by design of virtualisation). The search input at the top of the dropdown
// is the primary way to reach items past the visible range.

export default function VirtualizedDropdownList<T>(
  props: VirtualizedDropdownListProps<T>,
): React.ReactElement {
  const {
    items,
    getItemKey,
    renderLabel,
    onSelect,
    isSelected,
    itemTestId,
    emptyMessage = "No results",
    maxHeight = MAX_HEIGHT_PX,
    minWidth = MIN_WIDTH_PX,
    estimateSize = ESTIMATE_SIZE_PX,
    "aria-label": ariaLabel,
  } = props;

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: OVERSCAN,
  });

  if (items.length === 0) {
    return (
      <div
        style={{ minWidth }}
        className="col-span-full px-3.5 py-2.5 text-sm text-zinc-500 sm:px-3 sm:py-1.5"
        aria-live="polite"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      style={{ maxHeight, minWidth, overflowY: "auto" }}
      className="col-span-full"
      data-testid="virtualized-dropdown-list"
      aria-label={ariaLabel}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
        role="presentation"
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          const selected = isSelected?.(item) ?? false;
          return (
            <div
              key={getItemKey(item)}
              data-index={virtualRow.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: estimateSize,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="overflow-hidden"
              role="presentation"
            >
              <DropdownItem
                data-cy={itemTestId?.(item)}
                onClick={() => onSelect(item)}
                aria-current={selected ? "true" : undefined}
                className={clsx("w-full truncate", {
                  "bg-brand-light text-gray-100": selected,
                })}
              >
                {renderLabel(item)}
              </DropdownItem>
            </div>
          );
        })}
      </div>
    </div>
  );
}
